<template>
  <ul
    v-show="isVisible"
    :id="`${prefix}-suggestion-list`"
    :class="classes"
    role="listbox"
  >
    <li
      v-for="(suggestion, index) in suggestions"
      :id="`${prefix}-suggestion-option-${index}`"
      :key="index"
      :class="{ selected: index === selected }"
      class="suggestion-item"
      data-value="ccc.edu"
      data-test-id="suggestion-item"
      role="option"
      :aria-selected="String(index === selected)"
      @mouseover="setSelected(index)"
      @focus="setSelected(index)"
    >
      <a
        v-safe-html="markHtml(suggestion)"
        class="suggestion-link"
        :href="getSuggestionUrl(suggestion)"
        tabindex="0"
        data-test-id="suggestion-link"
        rel="noopener"
        @click.prevent="triggerSearch(suggestion)"
      />
    </li>
  </ul>
</template>

<script>
import { createMarkHtml } from '~~/common/js/universal/utils';
import fetch, { AbortController } from '~~/common/js/universal/fetch';
import screen from '~~/common/vue/mixins/screen';
import classes from '~~/common/vue/mixins/classes';

let cache = {};
let cancelRequest = () => {};

export const variants = ['default', 'compact'];
export const MAX_SUGGESTIONS_DESKTOP = 5;
export const MAX_SUGGESTIONS_MOBILE = 8;

export default {
  name: 'ESuggestionList',
  mixins: [classes, screen],
  props: {
    inputText: {
      type: String,
      default: '',
    },
    autocompleteUrl: {
      type: String,
      required: true,
    },
    market: {
      type: String,
      default: 'en-us',
    },
    /**
     * One of `default, compact`
     */
    variant: {
      type: String,
      cssClass: true,
      default: 'default',
      validator: (variant) => variants.includes(variant),
    },
    searchPath: {
      type: String,
      required: true,
    },
    prefix: {
      type: String,
      default: 'search-form',
    },
  },
  data() {
    return {
      suggestions: [],
      selected: -1,
    };
  },
  computed: {
    markHtml() {
      return createMarkHtml(this.inputText, 'suggestion-highlight', 'suggestion-highlight');
    },
    isVisible() {
      return this.suggestions.length > 0;
    },
    limit() {
      return this.isDesktopUp ? MAX_SUGGESTIONS_DESKTOP : MAX_SUGGESTIONS_MOBILE;
    },
  },
  watch: {
    inputText: 'fetchSuggestions',
    isVisible(visible) {
      this.$emit('visibilityChange', visible);
    },
    selected(index) {
      const selectedOptionId = index === -1 ? '' : `${this.prefix}-suggestion-option-${index}`;
      this.$emit('selectOption', selectedOptionId);
    },
  },
  mounted() {
    this.$parent.$on('clearSuggestions', () => this.clearSuggestions());
    this.$parent.$on('getSuggestions', () => this.fetchSuggestions(this.inputText));
    this.$parent.$on('onKeyDown', (key) => {
      if (key === 'esc') {
        this.clearSuggestions();
      }
      if (key === 'up') {
        this.selected -= 1;
      } else if (key === 'down') {
        this.selected += 1;
      } else {
        return;
      }
      if (this.selected > this.suggestions.length) {
        this.selected = 0;
      } else if (this.selected <= -1) {
        this.selected = this.suggestions.length;
      }
      this.$emit('updateInputText', this.suggestions[this.selected]);
    });
  },
  methods: {
    clearSuggestions() {
      this.selected = -1;
      this.suggestions = [];
    },
    clearCache() {
      cache = {};
    },
    getSuggestions(query, market) {
      const controller = new AbortController();
      const params = new URLSearchParams({ q: query, mkt: market });
      const url = `${this.autocompleteUrl}?${params.toString()}`;
      const request = fetch(url, { signal: controller.signal });
      const cancel = () => controller.abort();
      const result = request.then((resp) => resp.json());
      return { cancel, result };
    },
    async fetchSuggestions(text) {
      this.selected = -1;
      cancelRequest();
      if (!text) {
        this.clearSuggestions();
        return;
      }
      if (cache[text]) {
        this.suggestions = cache[text];
        return;
      }
      try {
        const { result, cancel } = await this.getSuggestions(text, this.market);
        cancelRequest = cancel;
        const { suggestions } = await result;
        if (suggestions.length === 0) {
          return;
        }
        this.suggestions = suggestions.slice(0, this.limit);
        cache[text] = this.suggestions;
      } catch (err) {
        // Do nothing
      }
    },
    setSelected(selected) {
      this.selected = selected;
    },
    triggerSearch(suggestion) {
      this.clearSuggestions();
      this.$emit('triggerSuggestionSearch', suggestion);
    },
    // We don't actually use the `<a>` elements in the list
    // to navigate to the new search, but we still want them to have
    // valid search URLs as `href` - that way, for example, users on iOS
    // can force-touch on the suggestions and already see a preview
    // of the SERP with the new results.
    getSuggestionUrl(suggestion) {
      try {
        const url = new URL(this.searchPath, process.env.searchUrl || 'https://ecosia.org');
        url.searchParams.set('q', suggestion);
        return url.toString();
      } catch (err) {
        return '#';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.suggestion-list {
  @include elevation(1);

  position: absolute;
  top: 100%;
  left: 0;
  // account for border width
  width: calc(100% - #{$space-4l - $border-width});
  z-index: $z-index-3l;
  padding: $space-1s 0;
  margin: 0 0 $space-s 0;
  text-align: left;
  list-style-type: none;
  background-color: var(--color-background-primary);
  border: solid $border-width var(--color-form-border-default);
  border-bottom-right-radius: $border-radius-l;
  border-bottom-left-radius: $border-radius-l;
  border-top: 0;
  font-size: $font-l;

  @include tablet {
    font-size: $font-l;
  }
}

.suggestion-item {
  padding-left: 0;
  color: var(--color-text-primary);

  &.selected {
    a {
      color: var(--color-text-primary);
    }
  }

  &:hover,
  &:active,
  &.selected {
    background: var(--color-highlight-primary);
  }
}

.suggestion-link {
  display: block;
  width: 100%;
  padding: 0 $space-m;
  line-height: $space-4l;
  font-weight: $font-weight-1l;
  color: var(--color-text-primary);
  text-decoration: none;
  cursor: pointer;
  max-width: 100%;
  box-sizing: border-box;

  &:hover,
  &:visited,
  &:active {
    color: var(--color-text-primary);
    text-decoration: none;
  }

  @include tablet {
    line-height: $space-3l;

    .suggestion-list--variant-compact & {
      padding-left: $space-m;
    }
  }
}
</style>

<style lang="scss">
.suggestion-highlight {
  font-weight: $font-weight-m;
  color: inherit;
  background: transparent;
}
</style>
